import { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';

import IncomingMessage from './IncomingMessage';
import OutgoingMessage from './OutgoingMessage';
import { useAppDispatch } from '../../../store/hooks';
import { NUMBERS } from '../../../resources/number-constants';
import { getChats, jobAIChat, addJobFieldSuggestion } from '../../../store/features/dashboard';
import { backBtn, chatPlaneIcon, climateLogo, proceedArrow } from '../../../assets/images/images';
import { UserCompanyDetails, ChatMessage, useFetchUserDetails } from '../../../utility/chatUtils';
import { getFieldLabelFromKey } from '../../../utility/chatSections';

interface ChatWithJobProps {
    conversationId: any;
    generateJobDescription: () => Promise<void> | void;
    jobId: any;
    step: any;
    hideChatWindow: boolean;
    setHideChatWindow: (flag: boolean) => void;
    refreshJobData: (isRefresh?: boolean) => void;
}

const ChatWithJob = ({
    conversationId,
    generateJobDescription,
    jobId,
    step,
    hideChatWindow,
    setHideChatWindow,
    refreshJobData
}: ChatWithJobProps) => {
    const handleKeyActivate = (e: React.KeyboardEvent, onActivate: () => void) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onActivate();
        }
    };
    const dispatch = useAppDispatch();
    const [chatList, setChatList] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const messageRef = createRef<HTMLTextAreaElement>();
    const [inputMessage, setInputMessage] = useState('');
    const [lastMessageId, setLastMessageId] = useState<number | null>(null);
    const [showSendButton, setShowSendButton] = useState(false);
    const [userCompanyDetails, setUserCompanyDetails] = useState<UserCompanyDetails>({ logo: '', userId: '' });
    const [enableGenerateResponse, setEnableGenerateResponse] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    const chatContainerRef = useRef<HTMLDivElement>(null); // Ref for chat container

    // Scroll to the bottom of chat
    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    // Input change handler for trimming spaces and showing send button
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const trimmedInput = e.target.value.trim(); // Trim input in real-time

        // Update the state only if the message contains any non-space characters
        setInputMessage(trimmedInput.length > 0 ? e.target.value : '');
        if (trimmedInput.length > 0) {
            setShowSendButton(true);
        } else {
            setShowSendButton(false);
        }
    };

    // Fetch chat details with loader handling
    const fetchChatDetails = useCallback(async () => {
        try {
            setIsLoading(true); // Show loader
            // Pass the step to determine which endpoint to use
            // The endpoint URL itself identifies which page we're on
            const payload = { conversation_id: conversationId, limit: 300, step };
            const res = await dispatch(getChats(payload)).unwrap();

            // Check if we need to add initial message after loading chat history
            const hasGeneralInfoMessages = res?.data?.some((msg: any) =>
                msg.result?.general_info === true || msg.result?.out_of_context === true
            );



            setChatList(res?.data || []);

            // Add initial message if there are general info messages and no existing initial message
            if (hasGeneralInfoMessages) {
                const hasExistingInitialMessage = res?.data?.some((msg: any) =>
                    msg.result?.is_initial === true
                );

                if (!hasExistingInitialMessage) {
                    setTimeout(() => addInitialMessage(), 100);
                }
            }


        } catch (error) {
            console.info('Error during fetching chat details:', error);
        } finally {
            setIsLoading(false); // Hide loader
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, conversationId, step]);


    // Add initial message after any AI response
    const addInitialMessage = useCallback(() => {
        const convId = conversationId;
        if (!convId) {
            return;
        }

        const initialMsg: ChatMessage = {
            id: `temp_${Date.now()}`, // Use temporary ID that won't be used as parent_id
            payload_type: 4, // NUMBERS.FOUR - required for IncomingMessage to render
            payload: { message: "Do you wish to improve your job description further? I can suggest some improvements. Which part would you like to work around?" },
            result: {
                message: "Do you wish to improve your job description further? I can suggest some improvements. Which part would you like to work around?",
                from_ai: true,
                is_initial: true,
                title: {
                    main_work_on: 'What would you work on?',
                },
            },
            conversation_id: convId,
        };

        setChatList((prev: ChatMessage[]) => [...prev, initialMsg]);
    }, [conversationId]);

    // Trigger initial message when chat list is empty and not loading
    useEffect(() => {
        if (!isLoading && chatList.length === 0 && conversationId) {
            addInitialMessage();
        }
    }, [isLoading, chatList.length, conversationId, addInitialMessage]);

    // Handle suggestion selection for job description sections
    const handleSelectSuggestion = async (text: string, sectionKey: string) => {
        const convId = conversationId;
        if (!convId) {
            return;
        }
        try {
            setIsLoading(true);
            // Send the suggestion to the backend with the target section
            const payload = {
                payload: {
                    title: { [sectionKey]: text },
                    message: null,
                },
                is_multi_select: false,
                conversation_id: convId,
                payload_type: NUMBERS.THREE,
                user: userCompanyDetails?.userId,
                parent_id: lastMessageId,
            };

            const response = await dispatch(jobAIChat({ payload, id: jobId, step })).unwrap();

            if (response?.data) {
                setChatList((prevChatList: any) => [...prevChatList, response.data]);
                if (response?.data?.id) {
                    setLastMessageId(response.data.id);
                }


            }
        } catch (error) {
            console.info('Error adding suggestion to job description:', error);
            // Add error message to chat
            const errorMsg = {
                id: `temp_${Date.now()}`,
                payload_type: NUMBERS.FOUR,
                payload: { message: `Failed to add suggestion to ${sectionKey}. Please try again.` },
                result: {
                    message: `Failed to add suggestion to ${sectionKey}. Please try again.`,
                    from_ai: true,
                },
                conversation_id: convId,
                is_local: true, // Mark as local message
            };
            setChatList((prev: any) => [...prev, errorMsg]);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle field suggestion addition using the new API endpoint
    const handleAddFieldSuggestion = async (fieldName: string, suggestions: string[]) => {
        if (!jobId) {
            return;
        }

        try {
            setIsLoading(true);

            const displayFieldName = getFieldLabelFromKey(fieldName, 'job');

            const payload = {
                job_id: jobId,
                field_name: fieldName,
                suggestions: suggestions,
                action: 'add'
            };

            const response = await dispatch(addJobFieldSuggestion(payload)).unwrap();

            if (response) {
                // Add success message to chat
                const successMsg = {
                    id: `temp_${Date.now()}`,
                    payload_type: NUMBERS.FOUR,
                    payload: { message: `Successfully added ${suggestions.length} suggestion(s) to ${displayFieldName}` },
                    result: {
                        message: `Successfully added ${suggestions.length} suggestion(s) to ${displayFieldName}`,
                        from_ai: true,
                    },
                    conversation_id: conversationId,
                    is_local: true, // Mark as local message
                };
                setChatList((prev: any) => [...prev, successMsg]);

                // Refresh job data to show the new suggestions in the form
                if (typeof refreshJobData === 'function') {
                    refreshJobData(false);
                }

                // Add initial message after successful suggestion addition
                setTimeout(() => addInitialMessage(), 100);


            }
        } catch (error) {
            console.info('Error adding field suggestion:', error);
            // Add error message to chat
            const errorMsg = {
                id: `temp_${Date.now()}`,
                payload_type: NUMBERS.FOUR,
                payload: { message: `Failed to add suggestions to ${fieldName}. Please try again.` },
                result: {
                    message: `Failed to add suggestions to ${fieldName}. Please try again.`,
                    from_ai: true,
                },
                conversation_id: conversationId,
                is_local: true, // Mark as local message
            };
            setChatList((prev: any) => [...prev, errorMsg]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchUserDetails = useFetchUserDetails();

    // Memoize requestPayload to avoid recreation on every render
    const requestPayload = useCallback((options: any) => {
        const body: any = {
            payload: {
                title: inputMessage ? null : options,
                message: inputMessage ? options : null,
            },
            is_multi_select: Array.isArray(options) && options?.length > 1,
            conversation_id: conversationId,
            payload_type: NUMBERS.THREE,
            user: userCompanyDetails?.userId,
        };

        // Only include parent_id if it's a valid backend ID (not a temporary local ID)
        const isValidParentId = !!lastMessageId &&
            typeof lastMessageId === 'number' &&
            Array.isArray(chatList) &&
            chatList.some((m: any) => m?.id === lastMessageId && !m?.is_local);
        if (isValidParentId) {
            body.parent_id = lastMessageId;
        }

        return body;
    }, [lastMessageId, conversationId, userCompanyDetails, inputMessage, chatList]);

    // Send message handler with optimistic update
    const handleSendMessage = async (options: any, initialAnswer: any) => {
        if (messageRef.current) {
            messageRef.current.value = '';
            messageRef.current.style.height = '56px';
        }

        // Reset flag when starting a new flow (asking for new suggestions)
        // This happens when user asks for a different type of suggestions
        const isNewFlow = options && typeof options === 'object' &&
            (options.main_work_on);
        const isTextMessage = typeof options === 'string' && options.trim().length > 0;

        if (isNewFlow || isTextMessage) {
            // Note: ChatWithJob doesn't have hasSentInitialAfterSuggestion flag like ChatWithICP
            // The initial message logic is handled differently here
        }

        const payload = requestPayload(options);

        // Deep copy the payload object to avoid shallow copy issues
        const newMessage = {
            id: `temp_${Date.now()}`,
            payload: { ...payload.payload }, // Deep copy of the payload's `payload` object
            is_multi_select: payload.is_multi_select,
            parent_id: payload.parent_id,
            conversation_id: payload.conversation_id,
            payload_type: payload.payload_type,
            user: payload.user,
        };

        // Optimistically update chat list with outgoing message
        setChatList((prevChatList: any) => [...prevChatList, newMessage]);
        setShowSendButton(false);
        try {
            setIsLoading(true);
            const response = await dispatch(jobAIChat({ payload, id: jobId, step })).unwrap();
            setInputMessage('');

            // Add the incoming message from the API response directly
            if (response?.data) {
                setChatList((prevChatList: any) => [...prevChatList, response.data]);
                // Update lastMessageId from the AI response for next message
                if (response?.data?.id) {
                    setLastMessageId(response.data.id);
                }


            }
            setIsLoading(false);
        } catch (error) {
            console.info('Error sending message:', error);
            setIsLoading(false);
            // Optionally handle reverting optimistic update on error
        }
    };

    const send = (event: any) => {
        event.preventDefault();

        const messageInput = messageRef.current;

        if (messageInput) {
            const trimmedMessage = messageInput.value.trim();

            if (trimmedMessage) {
                setShowSendButton(true);
                handleSendMessage(trimmedMessage, null);
                messageInput.value = '';
                messageInput.style.height = '56px';
            }
        }
    };

    const adjustTextareaSizeAndNotifyTyping = (event: any) => {
        const { value, scrollHeight } = event?.target;
        const isEmpty = value === '' || value === '\n';

        if (isEmpty) {
            event.target.style.height = '56px';
        } else {
            event.target.style.height = `${scrollHeight}px`;
        }
    };

    const handleEnterKeyPress = (event: any) => {
        if ((event.key === 'Enter') && !event.shiftKey) {
            event.preventDefault();
            send(event);
        } else if ((event.key === 'Enter') && event.shiftKey) {
            event.stopPropagation();
        }
    };

    // Scroll to bottom when chat list is updated or loader changes
    useEffect(() => {
        if (chatList.length) {
            scrollToBottom();
        }
    }, [chatList, isLoading]);

    useEffect(() => {
        const convId = conversationId;
        if (!hasLoaded && convId) {
            fetchChatDetails();
            fetchUserDetails().then(setUserCompanyDetails);
            setHasLoaded(true);
        }
    }, [fetchChatDetails, fetchUserDetails, conversationId, hasLoaded, step]);
    return (
        <>
            <div className="container">
                <div className="client-chat-main">
                    <div className="messaging">
                        <div className="inbox_msg">
                            <div className="chat-ai-profile-wrap">
                                {!hideChatWindow && (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip>
                                                Back
                                            </Tooltip>
                                        }
                                    >
                                        <div className="back-btn">
                                            <Image
                                                src={backBtn}
                                                height={25}
                                                width={25}
                                                alt="Back"
                                                className="back-btn-arrow"
                                                onClick={() => setHideChatWindow(true)}
                                                role="button"
                                                aria-label="Back"
                                                tabIndex={0}
                                                onKeyDown={(e) => handleKeyActivate(e, () => setHideChatWindow(true))}
                                            />
                                        </div>
                                    </OverlayTrigger>
                                )}
                                <h3 className="chat-profile-head">
                                    Hunter AI - Job Description
                                </h3>
                                <img src={climateLogo} alt="profile" className="chat-ai-profile-img" />
                            </div>
                            <div className="mesgs" ref={chatContainerRef}>
                                <div className="msg_history">

                                    {chatList?.length > 0 && chatList.map((chat: any, index: number) => (
                                        <div key={chat?.id || chat?.timestamp} className='incoming-outgoing-wrap'>
                                            {(chat?.payload_type !== NUMBERS.FOUR) && (
                                                <OutgoingMessage
                                                    chat={chat}
                                                    companyLogo={userCompanyDetails?.logo}
                                                    isFromJob={true}
                                                />
                                            )}
                                            {(chat?.payload_type === NUMBERS.FOUR) && (
                                                <IncomingMessage
                                                    chat={chat}
                                                    isLoading={isLoading}
                                                    setEnableGenerateResponse={setEnableGenerateResponse}
                                                    message={chat?.result}
                                                    setLastMessageId={setLastMessageId}
                                                    handleSendMessage={handleSendMessage}
                                                    isFromJob={true}
                                                    componentType="job"
                                                    onSelectSuggestion={handleSelectSuggestion}
                                                    onAddFieldSuggestion={handleAddFieldSuggestion}
                                                    isLastMessage={index === chatList.length - 1}
                                                />
                                            )}
                                        </div>
                                    ))}

                                    {isLoading && (
                                        <div className="wrapper bounce-loader-wrapper">
                                            <div className="circle"></div>
                                            <div className="circle"></div>
                                            <div className="circle"></div>
                                        </div>
                                    )}
                                </div>

                                <div className="textarea-chat-row">
                                    <div className="textarea-wrap">
                                        <textarea
                                            className="form-control"
                                            id="exampleFormControlTextarea1"
                                            rows={3}
                                            onInput={adjustTextareaSizeAndNotifyTyping}
                                            placeholder="Type here to ask anything"
                                            onChange={handleInputChange}
                                            ref={messageRef}
                                            onKeyDown={(e) => handleEnterKeyPress(e)}
                                        />
                                        <img
                                            src={chatPlaneIcon}
                                            alt="send"
                                            className={`chat-send-btn ${showSendButton ? '' : 'd-none'}`} // Show/hide button based on input
                                            onClick={() => handleSendMessage(inputMessage, null)} // Send message on click
                                            role="button"
                                            aria-label="Send message"
                                            tabIndex={0}
                                            onKeyDown={(e) => handleKeyActivate(e, () => handleSendMessage(inputMessage, null))}
                                        />
                                    </div>
                                    <Button
                                        className={`generate-btn d-none ${!enableGenerateResponse ? 'disabled' : ''}`}
                                        onClick={generateJobDescription}
                                    >
                                        Generate
                                        <span className='btn-right-img'><img src={proceedArrow} alt="generate" /></span>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ChatWithJob;
